/*
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

/* eslint-disable import/prefer-default-export */
import React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import isNil from 'lodash/isNil';
import size from 'lodash/size';
import { selectUserData } from '../store';
import { Authorities } from '../shared/entities/user';
import { IDToken } from '@okta/okta-auth-js';

export const areAllowedRolesPresent = (userData?: IDToken, ...allowedRoles: Authorities[]) => {
  return (
    !isNil(userData) &&
    !isNil(userData.claims) &&
    !isNil(userData.claims['authorities']) &&
    size(
      (userData.claims['authorities'] as Authorities[]).filter((authority: Authorities) => {
        return allowedRoles.includes(authority);
      }),
    ) > 0
  );
};

export const requireRBACRoles = <Props extends object>(
  FeatureComponent: React.ComponentType<Props>,
  ...allowedRoles: Authorities[]
) => {
  const FeatureFlagHOC = (props: Props) => {
    const userData = useSelector(selectUserData, shallowEqual);

    if (!isNil(userData) && areAllowedRolesPresent(userData, ...allowedRoles)) {
      return <FeatureComponent {...props} />;
    }

    return null;
  };

  return FeatureFlagHOC;
};

const ROLE_ORGID_PREFIX = 'ROLE_ORGID_';

export const retrieveOrganizationNumber = (userData?: IDToken): number | undefined => {
  // Return undefined if the list of roles don't exist within userData
  if (isNil(userData) || isNil(userData.claims) || isNil(userData.claims['authorities'])) {
    return undefined;
  }

  // Find the role that contains the user's Org ID (ie "ROLE_ORGID_<number>") and extract the number
  const orgIdAuthority = (userData.claims['authorities'] as String[]).find((authority: String) =>
    authority.startsWith(ROLE_ORGID_PREFIX),
  );

  // Return undefined if the role with Org ID prefix is not found
  if (isNil(orgIdAuthority)) {
    return undefined;
  }

  // Extract the Org ID number from the authority string
  const orgIdString = orgIdAuthority.replace(ROLE_ORGID_PREFIX, '');
  const orgIdNumber = parseInt(orgIdString, 10);

  // Return undefined if the extracted Org ID is not a valid number
  if (isNaN(orgIdNumber)) {
    return undefined;
  }

  return orgIdNumber;
};
